/**
 * In-feed app install banner for the Performer page (mobile only).
 * Gated by experiment MPerformerAppInstallInFeedBanner (ID: 102836).
 * Figma: https://www.figma.com/design/O2XQo8C3FibQ2zWTGvD409/Direct-Design?node-id=493-64427
 * JIRA: https://thestubhub.atlassian.net/browse/DMS-141
 */

import { useCallback } from "react";
import { ButtonKind, ButtonLink, ButtonSize, ButtonVariant, Text } from "@stubhub/broadway-web";
import { StarFillIcon } from "@stubhub/broadway-web/icons/functional";
import { useContent, useFormattedContent } from "src/shared/components/content";
import { useAppContext } from "src/shared/context/AppContext";
import { sendAnalyticsEvent } from "src/shared/utils/analyticsevent/analytics-event";
import { useIsAndroidApp, useIsStubhub } from "src/shared/utils/appContextHelper";
import { APP_STORE_LINKS, COBRAND_IDENTIFIERS } from "src/website-apps/shared/constants/app-install";
import {
  getAppInstallQuickLinkUrl,
  AppInstallQuickLinkType,
} from "src/website-apps/shared/utils/getAppInstallQuickLinkUrl";
import {
  cardStyle,
  headlineStyle,
  bottomRowStyle,
  appInfoStyle,
  appLogoStyle,
  appNameRatingStyle,
  appNameStyle,
  ratingRowStyle,
  ratingScoreStyle,
  ratingStarsStyle,
  downloadButtonStyle,
} from "./AppInstallInFeedBanner.styles";
import type { AppInstallInFeedBannerPlacement } from "./types";

const FULL_STAR_COUNT = 5;
const STAR_ICON_SIZE = 12 as const;

/** Half star — grey base with orange star clipped to 50% width. */
const HalfStar = () => (
  <span
    style={{
      position: "relative",
      display: "inline-flex",
      width: STAR_ICON_SIZE,
      height: STAR_ICON_SIZE,
      flexShrink: 0,
    }}
  >
    <StarFillIcon size={STAR_ICON_SIZE} color="#E0E0E0" />
    <span
      style={{
        position: "absolute",
        insetInlineStart: 0,
        top: 0,
        width: "50%",
        height: "100%",
        overflow: "hidden",
        display: "inline-flex",
        flexShrink: 0,
      }}
    >
      <span style={{ display: "inline-flex", width: STAR_ICON_SIZE, height: STAR_ICON_SIZE, flexShrink: 0 }}>
        <StarFillIcon size={STAR_ICON_SIZE} />
      </span>
    </span>
  </span>
);

interface AppInstallInFeedBannerProps {
  hasEvents: boolean;
  placement: AppInstallInFeedBannerPlacement;
}

export const AppInstallInFeedBanner = ({ hasEvents, placement }: AppInstallInFeedBannerProps) => {
  const appContext = useAppContext();
  const { env } = appContext;
  const isSh = useIsStubhub();
  const isAndroid = useIsAndroidApp();
  const cobrandName = isSh ? COBRAND_IDENTIFIERS.SH : COBRAND_IDENTIFIERS.VGG;

  const handleDownloadClick = useCallback(() => {
    sendAnalyticsEvent(appContext, env, "AppInstallInFeedBannerDownloadClick", [{ placement, eventTime: Date.now() }]);
  }, [appContext, env, placement]);

  // cid$#4334368# — "Install the {0} app, stay updated on your favorite artists."
  const headlineWithEvents = useFormattedContent("cid$#4334368#", [cobrandName]);
  // cid$#4334396# — "There aren't events right now. Download the {0} app, stay updated on your favorite artists."
  const headlineNoEvents = useFormattedContent("cid$#4334396#", [cobrandName]);
  // cid$#4334404# — "{0} app"
  const appNameContent = useFormattedContent("cid$#4334404#", [cobrandName]);
  // cid$#4334409# — "Download"
  const downloadContent = useContent("cid$#4334409#");

  const appIconSrc = isSh
    ? `${appContext.env["REACT_APP_ASSETS_URL"]}/images/Assets/logo-app-sh.svg`
    : `${appContext.env["REACT_APP_ASSETS_URL"]}/images/Assets/logo-app-vgg.svg`;

  const appUrl =
    getAppInstallQuickLinkUrl({
      userId: appContext.userId,
      appContext,
      type: AppInstallQuickLinkType.PERFORMER,
      isStubhub: isSh,
    }) ?? APP_STORE_LINKS?.[cobrandName]?.[isAndroid ? "GOOGLE" : "APPLE"];

  return (
    <div data-testid="app-install-in-feed-banner" className={cardStyle}>
      <Text className={headlineStyle}>{hasEvents ? headlineWithEvents : headlineNoEvents}</Text>
      <div className={bottomRowStyle}>
        <div className={appInfoStyle}>
          <img src={appIconSrc} alt="" aria-hidden="true" className={appLogoStyle} />
          <div className={appNameRatingStyle}>
            <span className={appNameStyle}>{appNameContent}</span>
            <div className={ratingRowStyle}>
              <span className={ratingScoreStyle}>{isSh ? "4.8" : "4.3"}</span>
              <div className={ratingStarsStyle}>
                {Array.from({ length: isSh ? FULL_STAR_COUNT : FULL_STAR_COUNT - 1 }, (_, i) => (
                  <StarFillIcon key={i} size={STAR_ICON_SIZE} />
                ))}
                {!isSh && <HalfStar />}
              </div>
            </div>
          </div>
        </div>
        <ButtonLink
          kind={ButtonKind.Neutral}
          variant={ButtonVariant.Regular}
          size={ButtonSize.Small}
          classes={{ root: downloadButtonStyle }}
          href={appUrl}
          onClick={handleDownloadClick}
        >
          {downloadContent}
        </ButtonLink>
      </div>
    </div>
  );
};
